import React from 'react';
import { AlertTriangle, Calendar, Sparkles, TrendingUp, Plane } from 'lucide-react';
import MarkdownLite from '../MarkdownLite';
import { useTranslation } from '../../i18n';

/**
 * 17/06/2026 (Lot 4) — Tableau de bord RH dédié à Nadia Cheikh.
 *
 * Trois sections :
 *   1. **Chauffeurs sous pression** — `holidayPressureLossThisYear > 0` :
 *      ils ont déjà perdu de la satisfaction faute de congés. Si la perte
 *      cumulée dépasse 8 sat → alerte rouge (à 10 ils quittent en fin
 *      d'année, cf. `applyYearEndPressureDepartures`).
 *   2. **Prochains départs prévus** — chauffeurs idle avec solde ≥ 5j,
 *      classés par pression descendante (les plus à risque partent en
 *      premier via `triggerAutoHoliday`).
 *   3. **Soldes critiques** — chauffeurs avec solde > 20j (Nadia doit les
 *      planifier vite avant l'écrêtement du compteur en fin d'année).
 *
 * Le composant n'apparaît dans l'onglet « Progression » que si Nadia est
 * en poste. Sinon : `LockedPanel` invitant à la recruter.
 */
export function NadiaHRDashboard({ drivers = [], hiredSupport = {} }) {
  const { t: _t } = useTranslation();
  const hasNadia = !!hiredSupport['role-nadia-cheikh'];

  if (!hasNadia) {
    return (
      <div className="surface-card p-4 border-dashed" data-testid="nadia-dashboard-locked">
        <div className="flex items-start gap-3">
          <Sparkles className="w-5 h-5 text-slate-500 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-slate-300">Tableau de bord RH (Nadia Cheikh)</p>
            <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">
              <MarkdownLite text="Recrutez **Nadia Cheikh — Responsable RH** pour débloquer ce tableau de bord. Vous y verrez la pression congés de votre équipe, les prochains départs planifiés et les soldes critiques à arbitrer." />
            </p>
          </div>
        </div>
      </div>
    );
  }

  const pressured = drivers
    .filter(d => (d.holidayPressureLossThisYear || 0) > 0)
    .sort((a, b) => (b.holidayPressureLossThisYear || 0) - (a.holidayPressureLossThisYear || 0));

  const planned = drivers
    .filter(d => !d.onHoliday && (d.holidayDaysRemaining || 0) >= 5 && d.status !== 'training')
    .sort((a, b) => (b.holidayPressureLossThisYear || 0) - (a.holidayPressureLossThisYear || 0))
    .slice(0, 6);

  const critical = drivers
    .filter(d => (d.holidayDaysRemaining || 0) > 20)
    .sort((a, b) => (b.holidayDaysRemaining || 0) - (a.holidayDaysRemaining || 0));

  return (
    <div className="surface-card p-4" data-testid="nadia-dashboard">
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-4 h-4 text-accent" />
        <h3 className="text-sm font-semibold text-foreground">Tableau de bord RH — Nadia Cheikh</h3>
        <span className="text-[10px] text-slate-500 ml-2">Mise à jour quotidienne</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">

        {/* Bloc 1 — Pression congés */}
        <div className="bg-ink-900 rounded-md border border-ink-600 p-3" data-testid="nadia-pressure-block">
          <div className="flex items-center gap-1.5 mb-2">
            <AlertTriangle className="w-3.5 h-3.5 text-rose-400" />
            <span className="text-xs font-semibold text-rose-300">
              Sous pression ({pressured.length})
            </span>
          </div>
          {pressured.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic">
              Aucun chauffeur sous pression. Bonne planification !
            </p>
          ) : (
            <ul className="space-y-1.5">
              {pressured.slice(0, 5).map(d => {
                const loss = d.holidayPressureLossThisYear || 0;
                const isCritical = loss >= 8; // 10 = départ forcé fin d'année
                return (
                  <li
                    key={d.id}
                    className={`text-[11px] flex justify-between items-center px-1.5 py-1 rounded ${
                      isCritical ? 'bg-rose-950/30 border border-rose-700/40' : ''
                    }`}
                    data-testid={`nadia-pressure-${d.id}`}
                  >
                    <span className="text-slate-300 truncate">{d.name}</span>
                    <span className={isCritical ? 'text-rose-300 font-bold' : 'text-rose-400'}>
                      −{loss} sat
                    </span>
                  </li>
                );
              })}
              {pressured.length > 5 && (
                <li className="text-[10px] text-slate-500 italic pt-1">
                  + {pressured.length - 5} autre{pressured.length - 5 > 1 ? 's' : ''}
                </li>
              )}
            </ul>
          )}
        </div>

        {/* Bloc 2 — Prochains départs */}
        <div className="bg-ink-900 rounded-md border border-ink-600 p-3" data-testid="nadia-planned-block">
          <div className="flex items-center gap-1.5 mb-2">
            <Plane className="w-3.5 h-3.5 text-amber-400" />
            <span className="text-xs font-semibold text-amber-300">
              Prochains départs prévus
            </span>
          </div>
          {planned.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic">
              Aucun départ programmable (soldes &lt; 5 j).
            </p>
          ) : (
            <ul className="space-y-1.5">
              {planned.map((d, i) => (
                <li
                  key={d.id}
                  className="text-[11px] flex justify-between items-center px-1.5 py-1 rounded"
                  data-testid={`nadia-planned-${d.id}`}
                >
                  <span className="text-slate-300 truncate flex items-center gap-1.5">
                    <span className="text-[9px] text-slate-500 w-3">#{i + 1}</span>
                    {d.name}
                  </span>
                  <span className="text-amber-300">{d.holidayDaysRemaining || 0} j</span>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[10px] text-slate-500 italic mt-2 leading-tight">
            Je déclenche les départs au fil de l&apos;eau quand l&apos;équipe est dégagée (5 j par bloc).
          </p>
        </div>

        {/* Bloc 3 — Soldes critiques */}
        <div className="bg-ink-900 rounded-md border border-ink-600 p-3" data-testid="nadia-critical-block">
          <div className="flex items-center gap-1.5 mb-2">
            <TrendingUp className="w-3.5 h-3.5 text-cyan-400" />
            <span className="text-xs font-semibold text-cyan-300">
              Soldes &gt; 20 j ({critical.length})
            </span>
          </div>
          {critical.length === 0 ? (
            <p className="text-[11px] text-slate-500 italic">
              Aucun solde critique. Planification saine.
            </p>
          ) : (
            <>
              <ul className="space-y-1.5">
                {critical.slice(0, 5).map(d => (
                  <li
                    key={d.id}
                    className="text-[11px] flex justify-between items-center px-1.5 py-1 rounded bg-cyan-950/20 border border-cyan-700/30"
                    data-testid={`nadia-critical-${d.id}`}
                  >
                    <span className="text-slate-300 truncate">{d.name}</span>
                    <span className="text-cyan-300 font-bold">{d.holidayDaysRemaining || 0} j</span>
                  </li>
                ))}
                {critical.length > 5 && (
                  <li className="text-[10px] text-slate-500 italic pt-1">
                    + {critical.length - 5} autre{critical.length - 5 > 1 ? 's' : ''}
                  </li>
                )}
              </ul>
              <p className="text-[10px] text-slate-500 italic mt-2 leading-tight">
                À planifier rapidement avant l&apos;écrêtement annuel.
              </p>
            </>
          )}
        </div>
      </div>

      {/* Footer — récap chiffres clés */}
      <div className="mt-3 pt-3 border-t border-ink-700 flex items-center justify-between text-[10px] text-slate-500">
        <span className="flex items-center gap-1">
          <Calendar className="w-3 h-3" /> Rapport trimestriel détaillé envoyé dans la Messagerie tous les 90 jours.
        </span>
        <span>— Nadia Cheikh, Responsable RH</span>
      </div>
    </div>
  );
}

export default NadiaHRDashboard;
